/**
 * HTML entity decoding, for the one place core still meets HTML: the `contentHtml` a comment
 * arrives in (R8's node tree is built *from* it and it never leaves the parser).
 *
 * Invidious escapes with Crystal's `HTML.escape`, which emits exactly five entities —
 * `&amp; &lt; &gt; &quot; &#39;` — all of which appear in the committed fixtures. Numeric
 * references are handled generally anyway, because the *uploader's* text can contain any of them
 * and a decoder that leaves `&#8217;` on screen looks like a bug in us.
 *
 * R1: plain string work. No DOM, no `TextDecoder`.
 */

const NAMED: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

function namedEntity(name: string): string | null {
  const key = name.toLowerCase();
  // `NAMED[key]` alone would answer `&constructor;` with a function off `Object.prototype`.
  if (!Object.prototype.hasOwnProperty.call(NAMED, key)) return null;
  return NAMED[key] ?? null;
}

const ENTITY = /&(#[xX][0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]*);/g;

/**
 * One left-to-right pass, which is what makes it safe: `&amp;#39;` decodes to the *text*
 * `&#39;` rather than to an apostrophe. A second pass would turn an escaped entity into a live
 * one — the same double-decode class of bug as the auth callback token (§3), in the opposite
 * direction.
 *
 * An unknown or out-of-range reference is left verbatim rather than dropped: it is the
 * uploader's text, and inventing a replacement character loses information.
 */
export function decodeHtmlEntities(input: string): string {
  if (!input.includes('&')) return input;
  return input.replace(ENTITY, (whole: string, body: string): string => {
    if (body.charAt(0) !== '#') return namedEntity(body) ?? whole;

    const hex = body.charAt(1) === 'x' || body.charAt(1) === 'X';
    const code = hex ? Number.parseInt(body.slice(2), 16) : Number.parseInt(body.slice(1), 10);
    if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return whole;
    // A lone surrogate would produce an unpaired code unit that no consumer can render.
    if (code >= 0xd800 && code <= 0xdfff) return whole;
    return String.fromCodePoint(code);
  });
}
